/**
 * The Company Details form layout.
 *
 * Field order, labels and help text live here; the page just walks this list.
 * The server decides the values, the option lists and which fields are locked,
 * so nothing in this file has to change when a field becomes read-only.
 *
 * Copy is taken from the master onboarding wizard's Company details step, so a
 * CA reads the same wording in both places.
 */

export type SettingsFieldKind =
  | "text"
  | "select"
  | "textarea"
  | "secret"
  // ZOUR-19: a calendar day (`YYYY-MM-DD`) and a yes/no switch. Added for the
  // GST panel's registration date and its "GST applicable" toggle.
  | "date"
  | "toggle";

export interface SettingsFieldConfig {
  /** Must match the field name the API uses. */
  name: string;
  label: string;
  kind: SettingsFieldKind;
  /** Help text under the input. */
  hint?: string;
  /** Longer example, shown muted below the hint. */
  example?: string;
  placeholder?: string;
  /** Key into the API's `options` map. Required for `select`. */
  optionsKey?: string;
  rows?: number;
  /**
   * ZOUR-19 — where a suggested value for this field comes from, if anywhere.
   *
   * `zoho_registered_on` is Zoho Books' `tax_registered_date`. The page
   * resolves the key; the field config only names it, so the layout stays
   * declarative.
   */
  suggestionKey?: "zoho_registered_on";
  /** Wording for the button that applies the suggestion. */
  suggestionLabel?: string;
  /**
   * Hide the "Locked: …" footnote for this field on this page.
   *
   * The field stays READ-ONLY and the server still refuses the write — this
   * only silences the explanation. Added for the GST panel, where the GSTIN's
   * lock line and hint were two lines of text on a page that already had
   * plenty; the Company Details page keeps both.
   */
  hideLockReason?: boolean;
}

export interface SettingsSectionConfig {
  id: string;
  title: string;
  description?: string;
  fields: SettingsFieldConfig[];
}

export const COMPANY_DETAILS_SECTIONS: SettingsSectionConfig[] = [
  {
    id: "identity",
    title: "Registered identity",
    description:
      "How this company is registered. These come from your GST registration and are set when your workspace is created.",
    fields: [
      {
        name: "company_name",
        label: "Company legal name",
        kind: "text",
        hint: "Matches the name on the GST registration or PAN, not a trade name.",
      },
      { name: "gst_number", label: "GSTIN", kind: "text" },
      { name: "pan_number", label: "PAN", kind: "text" },
      {
        name: "app_url",
        label: "Workspace address",
        kind: "text",
        hint: "The web address your team uses for this company in Zournal.",
      },
    ],
  },
  {
    id: "business",
    title: "About the business",
    description:
      "What this company does. This shapes how Zournal classifies ledgers and tax treatment, so it is worth keeping accurate.",
    fields: [
      {
        name: "trading_name",
        label: "Trading name",
        kind: "text",
        placeholder: "The name customers know you by",
        hint: "Leave blank if you trade under the legal name.",
      },
      {
        name: "company_type",
        label: "Entity type",
        kind: "select",
        optionsKey: "entity_type",
      },
      {
        name: "business_nature_category",
        label: "Nature of business",
        kind: "select",
        optionsKey: "business_nature_category",
      },
      {
        name: "core_business",
        label: "What does this company do?",
        kind: "textarea",
        rows: 4,
        placeholder: "e.g. Manufactures and exports cotton textiles",
        hint: "Describe what the company sells and where the money comes from.",
        example:
          "Example: a wholesale fabric trader in Coimbatore. Revenue comes from bulk fabric sales to garment units, job-work processing charges, and occasional export sales. Purchases are mostly from local powerloom units, some on credit.",
      },
      {
        name: "website",
        label: "Website",
        kind: "text",
        placeholder: "https://",
      },
    ],
  },
  {
    id: "registered-address",
    title: "Registered address",
    description: "Used on documents and for determining place of supply.",
    fields: [
      { name: "address_line1", label: "Address line 1", kind: "text" },
      { name: "address_line2", label: "Address line 2", kind: "text" },
      { name: "state", label: "State", kind: "text" },
      { name: "pincode", label: "PIN code", kind: "text" },
      { name: "country", label: "Country", kind: "text" },
    ],
  },
  {
    id: "registrations",
    title: "Other registrations",
    fields: [
      {
        name: "cin_number",
        label: "CIN",
        kind: "text",
        hint: "Corporate Identity Number, for companies registered with the MCA.",
      },
      {
        name: "msme",
        label: "MSME",
        kind: "select",
        optionsKey: "msme",
        hint: "Affects payment-terms reporting under the MSMED Act.",
      },
    ],
  },
];
